import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  EconomicSectorObjectTypes,
  EconomicSectorObjectTypesRequestBody,
} from './types';

export const economicSectorObjectTypesApi = createApi({
  reducerPath: 'economicSectorObjectTypesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/economic-sector-object-types`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  tagTypes: ['EconomicSectorObjectType', 'EconomicSectorObjectTypes'],
  endpoints: builder => {
    return {
      getEconomicSectorObjectTypesI: builder.infiniteQuery<
        EconomicSectorObjectTypes[],
        FiltersGrid,
        Pagination
      >({
        infiniteQueryOptions,
        query: ({ queryArg, pageParam }) => ({
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        }),
        providesTags: () => [
          'EconomicSectorObjectType',
          'EconomicSectorObjectTypes',
        ],
      }),
      getEconomicSectorObjectTypeById: builder.query<
        EconomicSectorObjectTypes,
        string
      >({
        query: id => ({
          url: `/${id}`,
        }),
        providesTags: () => ['EconomicSectorObjectType'],
      }),
      createEconomicSectorObjectType: builder.mutation<
        EconomicSectorObjectTypes,
        EconomicSectorObjectTypesRequestBody
      >({
        query: data => ({
          url: '/',
          method: 'POST',
          data,
        }),
        invalidatesTags: result => [
          'EconomicSectorObjectType',
          { type: 'EconomicSectorObjectType', id: result?.id },
        ],
      }),
      updateEconomicSectorObjectType: builder.mutation<
        EconomicSectorObjectTypes,
        EconomicSectorObjectTypesRequestBody
      >({
        query: data => ({
          url: `/${data.id}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: result => [
          'EconomicSectorObjectType',
          { type: 'EconomicSectorObjectType', id: result?.id },
        ],
      }),
      deleteEconomicSectorObjectTypeById: builder.mutation<void, string>({
        query: id => ({
          url: `/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_, __, id) => [
          'EconomicSectorObjectType',
          { type: 'EconomicSectorObjectType', id },
        ],
      }),
    };
  },
});

export const {
  useCreateEconomicSectorObjectTypeMutation,
  useUpdateEconomicSectorObjectTypeMutation,
} = economicSectorObjectTypesApi;

export const useGetEconomicSectorObjectTypesIInfiniteQuery =
  normalizeInfiniteQueryData(
    economicSectorObjectTypesApi.useGetEconomicSectorObjectTypesIInfiniteQuery,
  );
